// @flow
import has from 'lodash/has'
import * as React from 'react'
import Flex from 'ui/atoms/Flex'
import FlexBox from 'ui/atoms/FlexBox'
import FormContainer from 'ui/atoms/FormContainer'
import ButtonClear from 'ui/ButtonClear'
import PrimaryButton from 'ui/PrimaryButton'

import cn from '@/utils/tailwind/cn'

type Props = {
  buttonsDisabled?: boolean,
  currentStep: ?number,
  extraSubmitButton?: ?{
    label: string,
    onClick: () => Promise<?boolean>,
    disabled?: boolean,
    loading?: boolean,
  },
  hideBar?: boolean,
  isKiosk?: boolean,
  isOfficeUse?: boolean,
  isTablet?: boolean,
  loading?: boolean,
  loadingNext?: boolean,
  loadingPrev?: boolean,
  notFixedBottom?: boolean,
  onBack?: () => Promise<>,
  onNext?: ({ submit?: boolean }) => Promise<>,
  onSaveEditLater?: () => Promise<> | void,
  onSubmit: () => Promise<>,
  stripeError?: ?string,
  submitDisabled?: boolean,
  submitLabel?: string,
  submittingSave?: boolean,
  totalSteps: number,
  isInternal?: boolean,
  isAnonymousForm?: boolean,
}

const FormClientFooter = ({
  buttonsDisabled,
  currentStep,
  extraSubmitButton,
  hideBar,
  isKiosk,
  isOfficeUse,
  isTablet,
  loading,
  loadingNext,
  loadingPrev,
  notFixedBottom,
  onBack,
  onNext,
  onSaveEditLater,
  onSubmit,
  stripeError,
  submitDisabled,
  submitLabel,
  submittingSave,
  totalSteps,
  isInternal,
  isAnonymousForm,
}: Props): React.Node => {
  const [hasBack, setHasBack] = React.useState(false)
  const [hasSubmit, setHasSubmit] = React.useState(false)
  const [progress, setProgress] = React.useState(0)

  const showSaveEditLater = React.useMemo(
    () =>
      !isOfficeUse &&
      !isKiosk &&
      !isTablet &&
      Boolean(onSaveEditLater) &&
      !isInternal,
    [isOfficeUse, isKiosk, isTablet, onSaveEditLater, isInternal]
  )

  const hasExtraSubmitButton =
    has(extraSubmitButton, 'label') && has(extraSubmitButton, 'onClick')

  React.useEffect(() => {
    if (currentStep !== 0 && !currentStep) return

    const isLastStep = currentStep + 1 >= totalSteps
    setHasBack(currentStep > 0)
    setHasSubmit(isLastStep)
    setProgress(currentStep / totalSteps)
  }, [currentStep, totalSteps])

  const renderBackButton = React.useCallback(() => {
    if (!hasBack) return null

    return (
      <ButtonClear
        className="border"
        disabled={buttonsDisabled || loadingPrev}
        loading={loadingPrev}
        onClick={onBack}
      >
        Back
      </ButtonClear>
    )
  }, [buttonsDisabled, hasBack, loadingPrev, onBack])

  const renderProgressBar = React.useCallback(() => {
    if (hideBar || totalSteps <= 1) return null

    return (
      <div className="h-4 w-full rounded border bg-screen text-left">
        <div
          className="h-full bg-zentake transition-all duration-300 ease-in-out"
          style={{ width: `${progress * 100}%` }}
        />
      </div>
    )
  }, [hideBar, progress, totalSteps])

  const renderExtraButton = React.useCallback(() => {
    if (!hasExtraSubmitButton || !hasSubmit || !extraSubmitButton) return null

    return (
      <div className="flex h-full flex-col items-end gap-1 py-1">
        <PrimaryButton
          className="button-color whitespace-nowrap rounded-r-none"
          disabled={
            buttonsDisabled || submittingSave || !!extraSubmitButton?.disabled
          }
          loading={loading || !!extraSubmitButton?.loading}
          onClick={extraSubmitButton?.onClick}
        >
          {extraSubmitButton?.label}
        </PrimaryButton>
      </div>
    )
  }, [
    buttonsDisabled,
    extraSubmitButton,
    hasExtraSubmitButton,
    hasSubmit,
    loading,
    submittingSave,
  ])

  const renderSubmitButton = React.useCallback(() => {
    return (
      <PrimaryButton
        className={cn('button-color whitespace-nowrap', {
          'rounded-l-none': hasExtraSubmitButton,
        })}
        dataCy="submit-button"
        disabled={
          buttonsDisabled || submitDisabled || submittingSave || !!stripeError
        }
        loading={loading}
        onClick={onSubmit}
      >
        {submitLabel || 'Submit'}
      </PrimaryButton>
    )
  }, [
    buttonsDisabled,
    hasExtraSubmitButton,
    loading,
    onSubmit,
    stripeError,
    submitDisabled,
    submitLabel,
    submittingSave,
  ])

  const renderSaveForLaterButton = React.useCallback(() => {
    if (!showSaveEditLater || isInternal || isAnonymousForm) return

    return (
      <button
        className="ml-3 whitespace-nowrap text-base text-gray-500 underline"
        disabled={buttonsDisabled || submittingSave}
        onClick={onSaveEditLater}
      >
        Save and continue later
      </button>
    )
  }, [
    buttonsDisabled,
    isInternal,
    isAnonymousForm,
    onSaveEditLater,
    showSaveEditLater,
    submittingSave,
  ])

  const renderSaveAndLockButton = React.useCallback(() => {
    if (!isInternal) return null

    return (
      <button
        className="ml-3 whitespace-nowrap text-base text-gray-500 underline"
        disabled={buttonsDisabled || submittingSave}
        onClick={() => onSubmit({ lockInternalForm: true })}
      >
        Save and lock
      </button>
    )
  }, [buttonsDisabled, isInternal, onSubmit, submittingSave])

  const renderNextButton = React.useCallback(() => {
    return (
      <PrimaryButton
        className="button-color"
        dataCy="next-button"
        disabled={buttonsDisabled || submittingSave}
        loading={loadingNext}
        onClick={onNext}
      >
        Next
      </PrimaryButton>
    )
  }, [buttonsDisabled, loadingNext, onNext, submittingSave])

  const renderPrimaryButton = React.useCallback(() => {
    if (hasSubmit) {
      return renderSubmitButton()
    }

    return renderNextButton()
  }, [hasSubmit, renderNextButton, renderSubmitButton])

  return (
    <div
      className={cn(
        'safari-safe-area-bottom border-t bg-white shadow',
        !notFixedBottom && 'fixed bottom-0 left-0 right-0 z-[10]'
      )}
    >
      <div className="flex h-full w-full items-center py-3">
        <FormContainer noPaddingTop>
          <div className="sm:mx-6">
            <Flex $horizontalSpacing="small" align="center" justify="between">
              <FlexBox>{renderBackButton()}</FlexBox>
              <FlexBox flex={5}>{renderProgressBar()}</FlexBox>
              <FlexBox
                className="flex text-right"
                flex={hasExtraSubmitButton ? 2 : 1}
              >
                {renderExtraButton()}
                <div className="flex h-full flex-col items-end gap-1 py-1">
                  {renderPrimaryButton()}
                  {renderSaveForLaterButton()}
                  {renderSaveAndLockButton()}
                </div>
              </FlexBox>
            </Flex>
          </div>
        </FormContainer>
      </div>
    </div>
  )
}

export default FormClientFooter
